import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '@/contexts/AdminContext';

/**
 * Fetches active providers and their weekly schedules.
 * Builds a schedule map used by slotPropGetter for availability shading.
 * 
 * scheduleMap: Map<providerId, Map<dayOfWeek, { timeSlots: Set<string>, isAvailable }>>
 */
export default function useProviderSchedules({ siteFilter = [] }) {
  const { apiFetch } = useAdmin();
  const [providers, setProviders] = useState([]);
  const [scheduleMap, setScheduleMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef(new Map()); // Cache provider schedules to avoid refetch

  // Fetch provider list
  const fetchProviders = useCallback(async () => {
    try {
      const res = await apiFetch('/providers');
      const data = await res.json();
      const list = (data.providers || []).filter(p => {
        if (siteFilter.length === 0) return true;
        return p.sites?.some(s => siteFilter.includes(s));
      });
      setProviders(list);
      return list;
    } catch (err) {
      console.error('[useProviderSchedules] fetch error:', err);
      return [];
    }
  }, [apiFetch, siteFilter]);

  // Fetch schedule for a single provider (with cache)
  const fetchProviderSchedule = useCallback(async (providerId) => {
    if (cacheRef.current.has(providerId)) {
      return cacheRef.current.get(providerId);
    }

    try {
      const res = await apiFetch(`/providers/${providerId}`);
      const data = await res.json();
      const schedules = data.provider?.schedules || [];
      cacheRef.current.set(providerId, schedules);
      return schedules;
    } catch {
      return [];
    }
  }, [apiFetch]);

  // Build schedule map from all providers
  const buildScheduleMap = useCallback(async (providerList) => {
    const map = new Map();

    await Promise.all(
      providerList.map(async (provider) => {
        const schedules = await fetchProviderSchedule(provider.id);
        const dayMap = new Map();

        schedules.forEach(s => {
          // Only include schedules for sites in our filter
          if (siteFilter.length > 0 && !siteFilter.includes(s.siteKey)) return;

          const existing = dayMap.get(s.dayOfWeek);
          if (existing) {
            // Merge time slots from multiple schedule blocks for the same day
            s.timeSlots.forEach(slot => existing.timeSlots.add(slot));
            existing.isAvailable = existing.isAvailable || s.isAvailable;
          } else {
            dayMap.set(s.dayOfWeek, {
              timeSlots: new Set(s.timeSlots),
              isAvailable: s.isAvailable,
            });
          }
        });

        map.set(provider.id, dayMap);
      })
    );

    setScheduleMap(map);
  }, [fetchProviderSchedule, siteFilter]);

  // Load everything
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const list = await fetchProviders();
      if (!cancelled && list.length > 0) {
        await buildScheduleMap(list);
      }
      if (!cancelled) setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [fetchProviders, buildScheduleMap]);

  return { providers, scheduleMap, loading };
}
